import { fetchApi } from '@/services/api';

export interface ForecastReviewFilterOptions {
  families: Array<{ id: number; name: string; code: string }>;
  subfamilies: Array<{ id: number; name: string; family_id: number; code: string }>;
  brands: Array<{ id: number; name: string; code: string }>;
  brand_codes: string[];
  suppliers: Array<{ id: number; name: string }>;
  warehouses: Array<{ id: number; name: string; code: string }>;
}

export interface ForecastReviewSeriesPoint {
  period: string;
  value: number | null;
}

export interface ForecastReviewChartResponse {
  granularity: 'week' | 'month';
  view_type: 'units' | 'value';
  current_period: string;
  forecast_start_period: string | null;
  cycle: { id: number; cycle_id: string } | null;
  /** Timeline completo (inicio de año → última predicción). */
  periods?: string[];
  historical_sales: ForecastReviewSeriesPoint[];
  /** Ventas AA alineadas al mismo eje (periodo −52 sem / −12 meses). */
  last_year_sales?: ForecastReviewSeriesPoint[];
  forecast: ForecastReviewSeriesPoint[];
  forecast_upper_bound: ForecastReviewSeriesPoint[];
  forecast_lower_bound: ForecastReviewSeriesPoint[];
}

export interface ForecastReviewKpis {
  forecast_total: number;
  forecast_total_quantity: number;
  forecast_total_value: number;
  forecast_monthly_average?: number | null;
  forecast_period: string;
  forecast_period_start?: string;
  forecast_period_end?: string;
  forecast_period_label?: string;
  horizon_periods?: number;
  comparison_last_year_period: string;
  comparison_last_year_period_start?: string;
  comparison_last_year_period_end?: string;
  comparison_last_year_period_label?: string;
  comparison_last_year_total: number;
  comparison_recent_period: string;
  comparison_recent_period_start?: string;
  comparison_recent_period_end?: string;
  comparison_recent_period_label?: string;
  comparison_recent_total: number;
  variation_vs_last_year: number | null;
  variation_vs_recent_period: number | null;
  open_alerts_count: number;
  critical_alerts_count: number;
  products_with_relevant_deviation: number;
  cycle: { id: number; cycle_id: string } | null;
  granularity: 'week' | 'month';
  current_period: string;
  /** @deprecated use forecast_total */
  forecast_total_next_period?: number;
  reference_period?: string;
}

export interface ForecastReviewAlertClassification {
  abc_quantity: string | null;
  abc_value?: string | null;
  xyz_class: string | null;
}

export interface ForecastReviewAlertMetrics {
  avg_forecast: number;
  avg_recent_sales: number;
  avg_last_year_sales: number;
  forecast_total: number;
  recent_sales_total: number;
  last_year_sales_total: number;
  variation_vs_recent: number | null;
  variation_vs_last_year: number | null;
}

export interface ForecastReviewAlertsComparisonContext {
  granularity: 'week' | 'month';
  view_type: 'units' | 'value';
  horizon_periods: number;
  period_unit: string;
  forecast_period_label: string;
  recent_period_label: string;
  last_year_period_label: string;
}

export interface ForecastReviewAlertsImpactSummary {
  alert_count: number;
  alerted_product_count: number;
  scope_forecast_total: number;
  alerts_forecast_total: number;
  alerts_recent_total: number;
  alerts_last_year_total: number;
  delta_vs_recent: number;
  delta_vs_last_year: number;
  share_of_forecast_pct: number | null;
  impact_on_forecast_pct: number | null;
  variation_vs_recent: number | null;
  variation_vs_last_year: number | null;
}

export interface ForecastReviewAlert {
  id: number;
  severity: string;
  alert_type: string;
  alert_type_label?: string;
  level: string;
  element_id: number | null;
  element_code: string | null;
  element_name: string | null;
  message: string | null;
  payload?: Record<string, unknown>;
  impact_estimate?: number | null;
  status: string;
  created_at: string | null;
  alert_count?: number;
  product_id?: number | null;
  family_id?: number | null;
  brand_id?: number | null;
  family_name?: string | null;
  supplier_name?: string | null;
  supplier_id?: number | null;
  classification?: ForecastReviewAlertClassification;
  brand_name?: string | null;
  metrics?: ForecastReviewAlertMetrics;
  resolution_outcome?: 'approved' | 'rejected' | 'reactivated' | null;
}

export interface ForecastReviewAlertApprovePreview {
  alert_id: number;
  product_id: number;
  warehouse_id: number;
  alert_type: string;
  horizon_start: string;
  horizon_end: string;
  periods_count: number;
  target_quantity_per_period: number;
  rule_applied: string;
  no_sales_history: boolean;
  current_forecast_total_quantity: number;
  projected_forecast_total_quantity: number;
  delta_quantity: number;
  rows_in_horizon: number;
}

export interface ForecastReviewConsensusSyncSummary {
  planning_cycle_id: number | null;
  plan_version_id: number | null;
  consensus_lines_updated: number;
  consensus_values_refreshed: number;
  family_week_rows_rebuilt: number;
  skipped: boolean;
  skip_reason?: string | null;
}

export interface ForecastReviewAlertDemandUpdate {
  product_id: number;
  warehouse_id: number;
  horizon_start: string;
  horizon_end: string;
  periods_updated: number;
  rows_updated: number;
  target_quantity_per_period: number;
  rule_applied: string;
  no_sales_history: boolean;
  consensus_sync: ForecastReviewConsensusSyncSummary;
}

export interface ForecastReviewAlertActionResponse {
  alert: ForecastReviewAlert & {
    resolution_outcome?: 'approved' | 'rejected' | 'reactivated' | null;
    resolved_at?: string | null;
  };
  demand_update?: ForecastReviewAlertDemandUpdate;
}

export interface ForecastReviewBulkApprovePreview {
  alert_count: number;
  product_count: number;
  alert_count_by_type: Record<string, number>;
  current_forecast_total_quantity: number;
  projected_forecast_total_quantity: number;
  delta_quantity: number;
  preview_errors: Array<{ alert_id: number; detail: string; code: string }>;
  status_filter: string;
}

export interface ForecastReviewBulkActionResponse {
  requested_count: number;
  succeeded_count: number;
  failed_count: number;
  failures: Array<{ alert_id: number; detail: string; code: string }>;
  total_rows_updated: number;
}

export interface ForecastReviewModifyPreviewPeriod {
  period: string;
  forecast_quantity: number;
  forecast_value: number;
  last_year_sales_quantity: number;
  recent_sales_quantity: number;
  demand_prediction_id: number | null;
}

export interface ForecastReviewModifyPreview {
  product_id: number;
  warehouse_id: number;
  granularity: 'week' | 'month';
  horizon_start: string;
  horizon_end: string;
  periods: ForecastReviewModifyPreviewPeriod[];
  reference_labels: {
    last_year_window: string;
    recent_window: string;
  };
}

export interface ForecastReviewModifyDemandUpdate {
  product_id: number;
  warehouse_id: number;
  horizon_start: string;
  horizon_end: string;
  periods_updated: number;
  rows_updated: number;
  ai_model_id: number;
  consensus_sync: ForecastReviewConsensusSyncSummary;
}

export interface ForecastReviewModifyApplyResponse {
  demand_update: ForecastReviewModifyDemandUpdate;
}

type ForecastReviewAlertsQueryParams = ForecastReviewQueryParams & {
  level?: 'product' | 'family' | 'brand' | 'supplier';
  status?: string;
  page?: number;
  page_size?: number;
};

function toAlertsApiParams(params?: ForecastReviewAlertsQueryParams): Record<string, string | number | boolean> {
  const apiParams = toApiParams(params);
  if (params?.level) apiParams.level = params.level;
  if (params?.status) apiParams.status = params.status;
  if (params?.page != null) apiParams.page = params.page;
  if (params?.page_size != null) apiParams.page_size = params.page_size;
  return apiParams;
}
export const FORECAST_REVIEW_ALERTS_PAGE_SIZE = 10;

export interface ForecastReviewAlertsResponse {
  count: number;
  page: number;
  page_size: number;
  total_pages?: number;
  level: string;
  status: string;
  cycle: { id: number; cycle_id: string } | null;
  comparison_context?: ForecastReviewAlertsComparisonContext;
  impact_summary?: ForecastReviewAlertsImpactSummary;
  results: ForecastReviewAlert[];
}

export type ForecastReviewQueryParams = {
  cycle_id?: number;
  warehouse_id?: number;
  product_id?: number;
  family_id?: number;
  subfamily_id?: number;
  brand_id?: number;
  brand_code?: string;
  supplier_id?: number;
  date_from?: string;
  date_to?: string;
  include_inactive?: boolean;
  granularity?: 'week' | 'month' | 'weekly' | 'monthly';
  view_type?: 'units' | 'value';
  abc_quantity?: string;
  abc_value?: string;
  xyz_class?: string;
  adi_cv_class?: string;
  profile_code?: string;
  is_new_product?: boolean;
  is_obsolete?: boolean;
  is_on_demand?: boolean;
  is_seasonal?: boolean;
};

export interface ForecastReviewComparisonRow {
  level: string;
  element_id: number;
  element_code: string | null;
  element_name: string | null;
  family_id?: number | null;
  family_name?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  ai_model_name?: string | null;
  forecast_total: number;
  forecast_monthly_average?: number | null;
  last_year_total: number;
  last_year_diff_qty: number;
  last_year_variation_pct: number | null;
  last_year_status: string | null;
  last_year_trend?: Array<{ period: string; value: number }>;
  recent_total: number;
  recent_diff_qty: number;
  recent_variation_pct: number | null;
  recent_status: string | null;
  recent_trend?: Array<{ period: string; value: number }>;
  severity: string | null;
  classification?: ForecastReviewAlertClassification & {
    adi_cv_class?: string | null;
    is_new_product?: boolean;
    is_obsolete?: boolean;
    is_on_demand?: boolean;
    is_seasonal?: boolean;
  };
  product_count?: number;
}

export interface ForecastReviewComparisonResponse {
  recent_period_length: number;
  level: string;
  page: number;
  page_size: number;
  total_pages: number;
  count: number;
  sort_by: string;
  sort_dir: string;
  forecast_period_label: string;
  last_year_period_label: string;
  recent_period_label: string;
  granularity: 'week' | 'month';
  view_type: 'units' | 'value';
  cycle: { id: number; cycle_id: string } | null;
  results: ForecastReviewComparisonRow[];
}

export interface ForecastReviewClassificationBreakdownRow {
  class_value: string;
  product_count: number;
  forecast_total: number;
  last_year_total: number;
  recent_total: number;
  variation_vs_last_year: number | null;
  variation_vs_recent: number | null;
  open_alerts_count: number;
}

export interface ForecastReviewClassificationBreakdownResponse {
  dimension: string;
  dimension_label: string;
  granularity: 'week' | 'month';
  view_type: 'units' | 'value';
  cycle: { id: number; cycle_id: string } | null;
  forecast_period_label?: string | null;
  forecast_period_start?: string | null;
  forecast_period_end?: string | null;
  horizon_periods?: number;
  last_year_period_label?: string | null;
  last_year_period_start?: string | null;
  last_year_period_end?: string | null;
  recent_period_label?: string | null;
  recent_period_start?: string | null;
  recent_period_end?: string | null;
  results: ForecastReviewClassificationBreakdownRow[];
}

export interface ForecastReviewDataValidationSummary {
  cycle_id: number | null;
  cycle_label: string | null;
  step_available: boolean;
  step_metadata: Record<string, unknown> | null;
  /** @deprecated legacy — no mostrar en UI */
  filtered_anomaly_count: number;
  /** @deprecated legacy — no mostrar en UI */
  temporal_gap_product_count: number;
  products_in_scope: number;
  products_with_issues: number;
  products_with_issues_pct: number;
  products_with_gaps: number;
  products_with_gaps_pct: number;
  products_with_stockout: number;
  products_with_stockout_pct: number;
  products_with_outliers: number;
  products_with_outliers_pct: number;
  period_unit: 'week' | 'month' | 'day';
}

export interface ForecastReviewDataValidationProductRow {
  product_id: number;
  warehouse_id: number;
  sku: string | null;
  product_name: string | null;
  stockout_count: number;
  demand_outlier_count: number;
  gap_count: number;
  total_issue_count: number;
}

export interface ForecastReviewDataValidationProductsResponse {
  cycle_id: number | null;
  cycle_label: string | null;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: ForecastReviewDataValidationProductRow[];
}

export interface ForecastReviewDataValidationSeriesPoint {
  period: string;
  period_date: string;
  sales_quantity: number;
  is_gap: boolean;
  is_outlier: boolean;
  is_stockout: boolean;
  observed_quantity: number | null;
  reference_bound: number | null;
}

export interface ForecastReviewDataValidationStockoutSpan {
  start_date: string;
  end_date: string;
  week_count: number;
}

export interface ForecastReviewDataValidationProductSeriesResponse {
  cycle_id: number | null;
  cycle_label: string | null;
  period_unit: 'week' | 'month' | 'day';
  horizon_start: string | null;
  horizon_end: string | null;
  series: ForecastReviewDataValidationSeriesPoint[];
  stockout_spans: ForecastReviewDataValidationStockoutSpan[];
  gap_periods: string[];
  summary: {
    stockout_count: number;
    outlier_count: number;
    gap_count: number;
  };
}

export type ForecastReviewDataValidationQueryParams = ForecastReviewQueryParams & {
  anomaly_type?: 'stockout' | 'demand_outlier';
  severity?: string;
  search?: string;
  period_date_from?: string;
  period_date_to?: string;
  has_gaps?: boolean;
  has_stockout?: boolean;
  has_outlier?: boolean;
  page?: number;
  page_size?: number;
};

export const FORECAST_REVIEW_DATA_VALIDATION_PAGE_SIZE = 10;

function toDataValidationApiParams(
  params?: ForecastReviewDataValidationQueryParams
): Record<string, string | number | boolean> {
  const apiParams = toApiParams(params);
  if (params?.anomaly_type) apiParams.anomaly_type = params.anomaly_type;
  if (params?.severity) apiParams.severity = params.severity;
  if (params?.search) apiParams.search = params.search;
  if (params?.period_date_from) apiParams.period_date_from = params.period_date_from;
  if (params?.period_date_to) apiParams.period_date_to = params.period_date_to;
  if (params?.has_gaps != null) apiParams.has_gaps = params.has_gaps;
  if (params?.has_stockout != null) apiParams.has_stockout = params.has_stockout;
  if (params?.has_outlier != null) apiParams.has_outlier = params.has_outlier;
  if (params?.page != null) apiParams.page = params.page;
  if (params?.page_size != null) apiParams.page_size = params.page_size;
  return apiParams;
}

function toApiParams(params?: ForecastReviewQueryParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!params) return out;
  if (params.cycle_id != null) out.cycle_id = params.cycle_id;
  if (params.warehouse_id != null) out.warehouse_id = params.warehouse_id;
  if (params.product_id != null) out.product_id = params.product_id;
  if (params.family_id != null) out.family_id = params.family_id;
  if (params.subfamily_id != null) out.subfamily_id = params.subfamily_id;
  if (params.brand_id != null) out.brand_id = params.brand_id;
  if (params.brand_code) out.brand_code = params.brand_code;
  if (params.supplier_id != null) out.supplier_id = params.supplier_id;
  if (params.date_from) out.date_from = params.date_from;
  if (params.date_to) out.date_to = params.date_to;
  if (params.include_inactive) out.include_inactive = true;
  if (params.granularity) {
    const g = params.granularity;
    out.granularity = g === 'weekly' ? 'week' : g === 'monthly' ? 'month' : g;
  }
  if (params.view_type) out.view_type = params.view_type;
  if (params.abc_quantity) out.abc_quantity = params.abc_quantity;
  if (params.abc_value) out.abc_value = params.abc_value;
  if (params.xyz_class) out.xyz_class = params.xyz_class;
  if (params.adi_cv_class) out.adi_cv_class = params.adi_cv_class;
  if (params.profile_code) out.profile_code = params.profile_code;
  if (params.is_new_product != null) out.is_new_product = params.is_new_product;
  if (params.is_obsolete != null) out.is_obsolete = params.is_obsolete;
  if (params.is_on_demand != null) out.is_on_demand = params.is_on_demand;
  if (params.is_seasonal != null) out.is_seasonal = params.is_seasonal;
  return out;
}

export const forecastReviewService = {
  getFilters: async (params?: ForecastReviewQueryParams): Promise<ForecastReviewFilterOptions> => {
    return fetchApi<ForecastReviewFilterOptions>('forecast-review/filters/', {
      params: toApiParams(params),
    });
  },

  getChart: async (params?: ForecastReviewQueryParams): Promise<ForecastReviewChartResponse> => {
    return fetchApi<ForecastReviewChartResponse>('forecast-review/chart/', {
      params: toApiParams(params),
    });
  },

  getKpis: async (params?: ForecastReviewQueryParams): Promise<ForecastReviewKpis> => {
    return fetchApi<ForecastReviewKpis>('forecast-review/kpis/', {
      params: toApiParams(params),
    });
  },

  getAlerts: async (
    params?: ForecastReviewAlertsQueryParams
  ): Promise<ForecastReviewAlertsResponse> => {
    const apiParams = toAlertsApiParams(params);
    apiParams.page_size = params?.page_size ?? FORECAST_REVIEW_ALERTS_PAGE_SIZE;
    return fetchApi<ForecastReviewAlertsResponse>('forecast-review/alerts/', { params: apiParams });
  },

  getBulkApprovePreview: async (
    params?: ForecastReviewAlertsQueryParams
  ): Promise<ForecastReviewBulkApprovePreview> => {
    const apiParams = toAlertsApiParams(params);
    delete apiParams.page;
    delete apiParams.page_size;
    return fetchApi<ForecastReviewBulkApprovePreview>(
      'forecast-review/alerts/bulk-approve-preview/',
      { params: apiParams }
    );
  },

  bulkApproveAlerts: async (
    params?: ForecastReviewAlertsQueryParams,
    note?: string
  ): Promise<ForecastReviewBulkActionResponse> => {
    const apiParams = toAlertsApiParams(params);
    delete apiParams.page;
    delete apiParams.page_size;
    return fetchApi<ForecastReviewBulkActionResponse>('forecast-review/alerts/bulk-approve/', {
      method: 'POST',
      params: apiParams,
      body: note ? { note } : {},
    });
  },

  bulkRejectAlerts: async (
    params?: ForecastReviewAlertsQueryParams,
    note?: string
  ): Promise<ForecastReviewBulkActionResponse> => {
    const apiParams = toAlertsApiParams(params);
    delete apiParams.page;
    delete apiParams.page_size;
    return fetchApi<ForecastReviewBulkActionResponse>('forecast-review/alerts/bulk-reject/', {
      method: 'POST',
      params: apiParams,
      body: note ? { note } : {},
    });
  },

  getApprovePreview: async (
    alertId: number,
    params?: ForecastReviewQueryParams
  ): Promise<ForecastReviewAlertApprovePreview> => {
    return fetchApi<ForecastReviewAlertApprovePreview>(
      `forecast-review/alerts/${alertId}/approve-preview/`,
      { params: toApiParams(params) }
    );
  },

  approveAlert: async (
    alertId: number,
    params?: ForecastReviewQueryParams,
    note?: string
  ): Promise<ForecastReviewAlertActionResponse> => {
    return fetchApi<ForecastReviewAlertActionResponse>(
      `forecast-review/alerts/${alertId}/approve/`,
      {
        method: 'POST',
        params: toApiParams(params),
        body: note ? { note } : {},
      }
    );
  },

  rejectAlert: async (
    alertId: number,
    params?: ForecastReviewQueryParams,
    note?: string
  ): Promise<ForecastReviewAlertActionResponse> => {
    return fetchApi<ForecastReviewAlertActionResponse>(
      `forecast-review/alerts/${alertId}/reject/`,
      {
        method: 'POST',
        params: toApiParams(params),
        body: note ? { note } : {},
      }
    );
  },

  reactivateAlert: async (
    alertId: number,
    params?: ForecastReviewQueryParams,
    note?: string
  ): Promise<ForecastReviewAlertActionResponse> => {
    return fetchApi<ForecastReviewAlertActionResponse>(
      `forecast-review/alerts/${alertId}/reactivate/`,
      {
        method: 'POST',
        params: toApiParams(params),
        body: note ? { note } : {},
      }
    );
  },

  getModifyPreview: async (
    productId: number,
    params?: ForecastReviewQueryParams
  ): Promise<ForecastReviewModifyPreview> => {
    return fetchApi<ForecastReviewModifyPreview>(
      `forecast-review/products/${productId}/modify-preview/`,
      { params: toApiParams(params) }
    );
  },

  applyModifyForecast: async (
    productId: number,
    params: ForecastReviewQueryParams | undefined,
    periods: Array<{ period: string; quantity: number }>
  ): Promise<ForecastReviewModifyApplyResponse> => {
    return fetchApi<ForecastReviewModifyApplyResponse>(
      `forecast-review/products/${productId}/modify-apply/`,
      {
        method: 'POST',
        params: toApiParams(params),
        body: { periods },
      }
    );
  },

  getComparison: async (
    params?: ForecastReviewQueryParams & {
      recent_period_length?: number;
      level?: 'product' | 'family' | 'brand' | 'supplier';
      page?: number;
      page_size?: number;
      sort_by?: string;
      sort_dir?: 'asc' | 'desc';
    }
  ): Promise<ForecastReviewComparisonResponse> => {
    const apiParams = toApiParams(params);
    if (params?.recent_period_length != null) apiParams.recent_period_length = params.recent_period_length;
    if (params?.level) apiParams.level = params.level;
    if (params?.page != null) apiParams.page = params.page;
    if (params?.page_size != null) apiParams.page_size = params.page_size;
    if (params?.sort_by) apiParams.sort_by = params.sort_by;
    if (params?.sort_dir) apiParams.sort_dir = params.sort_dir;
    return fetchApi<ForecastReviewComparisonResponse>('forecast-review/comparison/', { params: apiParams });
  },

  getClassificationBreakdown: async (
    params?: ForecastReviewQueryParams & { dimension?: 'abc_quantity' | 'abc_value' | 'xyz_class' | 'adi_cv_class' }
  ): Promise<ForecastReviewClassificationBreakdownResponse> => {
    const apiParams = toApiParams(params);
    if (params?.dimension) apiParams.dimension = params.dimension;
    return fetchApi<ForecastReviewClassificationBreakdownResponse>(
      'forecast-review/classification-breakdown/',
      { params: apiParams }
    );
  },

  getDataValidationSummary: async (
    params?: ForecastReviewDataValidationQueryParams
  ): Promise<ForecastReviewDataValidationSummary> => {
    return fetchApi<ForecastReviewDataValidationSummary>('forecast-review/data-validation/summary/', {
      params: toDataValidationApiParams(params),
    });
  },

  getDataValidationProducts: async (
    params?: ForecastReviewDataValidationQueryParams
  ): Promise<ForecastReviewDataValidationProductsResponse> => {
    const apiParams = toDataValidationApiParams(params);
    apiParams.page_size = params?.page_size ?? FORECAST_REVIEW_DATA_VALIDATION_PAGE_SIZE;
    return fetchApi<ForecastReviewDataValidationProductsResponse>('forecast-review/data-validation/products/', {
      params: apiParams,
    });
  },

  getDataValidationProductSeries: async (
    productId: number,
    params?: ForecastReviewQueryParams
  ): Promise<ForecastReviewDataValidationProductSeriesResponse> => {
    return fetchApi<ForecastReviewDataValidationProductSeriesResponse>(
      `forecast-review/data-validation/products/${productId}/series/`,
      { params: toApiParams(params) }
    );
  },

  /** Búsqueda / slice de productos (mismo contrato que predictions legacy). */
  getProductFilterSliceOrSearch: async (params: {
    family?: number;
    brand?: number;
    supplier?: number;
    productQuery: string;
  }): Promise<Array<{ value: number; label: string }>> => {
    const q = params.productQuery.trim();
    const apiParams: Record<string, string | number> = {
      module: 'predictions',
      products_mode: q.length >= 2 ? 'search' : 'slice',
    };
    if (params.family) apiParams.family = params.family;
    if (params.brand) apiParams.category = params.brand;
    if (params.supplier) apiParams.supplier = params.supplier;
    if (q.length >= 2) apiParams.product_q = q;

    const data = await fetchApi<{ product?: Array<{ value: number; label: string }> }>('products/filter-options/', {
      params: apiParams as Record<string, string | number | boolean>,
    });
    const raw = data.product;
    if (!Array.isArray(raw)) return [];
    return raw.map((o) => ({ value: Number(o.value), label: o.label }));
  },
};
